// src/features/chat/ChatPoliciesPanel.tsx
//
// Chat policy admin — create/list/edit/delete ChatPolicy rows (scope entity
// + conversation defaults, limits, feature toggles) and look up the policy
// directly attached to a given entity. Mounted at /admin/chat, gated by
// AdminRoute in App.tsx. Same "admin, rare" altitude as calendar's
// settings → policies tab (docs/ux/calendar-ia.md §4) — chat has no
// equivalent conversation-settings surface to nest this under, so it gets
// its own admin page, same pattern as /admin/authz, /admin/context.

import { useEffect, useState } from 'react';
import { chatApi } from '@core/api';
import { useI18n } from '@react/hooks';
import { Button, Input, ErrorAlert, Badge, Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableEmpty } from '@react/components';
import { FormDialog, TitleInput } from '@/components/form';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import type { ChatPolicy } from '@core/types/chat';

const emptyForm = {
  name: '',
  description: '',
  scopeEntityType: '',
  scopeEntityId: '',
  maxMembersPerConversation: '',
  maxPinnedMessages: '',
  maxMessageLength: '',
  enableThreads: true,
  enableReactions: true,
  enableMentions: true,
  enableEmbeds: true,
  enableFileUploads: true,
};

export function ChatPoliciesPanel() {
  const { t } = useI18n();
  const [policies, setPolicies] = useState<ChatPolicy[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [isSaving, setIsSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);

  // "Lookup by entity" tool (GetChatPolicyByEntity) — directly attached
  // policy only, no inheritance resolution (that's the Resolve tab).
  const [lookupType, setLookupType] = useState('team');
  const [lookupId, setLookupId] = useState('');
  const [lookupResult, setLookupResult] = useState<ChatPolicy | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [isLookingUp, setIsLookingUp] = useState(false);

  const load = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await chatApi.ListChatPolicies();
      setPolicies(res.policies?.policies ?? []);
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  // Edit loads the full record via GetChatPolicy rather than trusting the
  // row already in `policies` — same "edit re-fetches" convention as
  // calendar's booking links (docs/ux/calendar-ia.md: "GetBookingLink ...
  // edit loads full record").
  const startEdit = async (row: ChatPolicy) => {
    const policyId = row.entity?.id;
    if (!policyId) return;
    setError(null);
    try {
      const res = await chatApi.GetChatPolicy(policyId);
      const policy = res.policy ?? row;
      setEditingId(policyId);
      setForm({
        name: policy.name ?? '',
        description: policy.description ?? '',
        scopeEntityType: policy.scope_entity_type ?? policy.scope_entity?.type_name ?? '',
        scopeEntityId: policy.scope_entity?.id ?? '',
        maxMembersPerConversation: String(policy.max_members_per_conversation ?? ''),
        maxPinnedMessages: String(policy.max_pinned_messages ?? ''),
        maxMessageLength: String(policy.max_message_length ?? ''),
        enableThreads: policy.enable_threads ?? true,
        enableReactions: policy.enable_reactions ?? true,
        enableMentions: policy.enable_mentions ?? true,
        enableEmbeds: policy.enable_embeds ?? true,
        enableFileUploads: policy.enable_file_uploads ?? true,
      });
      setShowForm(true);
    } catch (err) {
      setError(err);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setIsSaving(true);
    setError(null);
    try {
      const scopeEntity = form.scopeEntityId.trim()
        ? { type_id: '', type_name: form.scopeEntityType.trim(), id: form.scopeEntityId.trim(), platform_id: '', tenant_id: '', product_id: '' }
        : undefined;
      if (editingId) {
        const existing = policies.find(p => p.entity?.id === editingId);
        await chatApi.UpdateChatPolicy(editingId, {
          policy: {
            ...existing,
            entity: existing?.entity,
            name: form.name.trim(),
            description: form.description.trim(),
            scope_entity: scopeEntity,
            scope_entity_type: form.scopeEntityType.trim() || undefined,
            max_members_per_conversation: form.maxMembersPerConversation ? Number(form.maxMembersPerConversation) : undefined,
            max_pinned_messages: form.maxPinnedMessages ? Number(form.maxPinnedMessages) : undefined,
            max_message_length: form.maxMessageLength ? Number(form.maxMessageLength) : undefined,
            enable_threads: form.enableThreads,
            enable_reactions: form.enableReactions,
            enable_mentions: form.enableMentions,
            enable_embeds: form.enableEmbeds,
            enable_file_uploads: form.enableFileUploads,
          } as ChatPolicy,
        });
      } else {
        await chatApi.CreateChatPolicy({
          entity: scopeEntity,
          name: form.name.trim(),
          description: form.description.trim() || undefined,
          scope_entity: scopeEntity,
          scope_entity_type: form.scopeEntityType.trim() || undefined,
          max_members_per_conversation: form.maxMembersPerConversation ? Number(form.maxMembersPerConversation) : undefined,
          max_pinned_messages: form.maxPinnedMessages ? Number(form.maxPinnedMessages) : undefined,
          max_message_length: form.maxMessageLength ? Number(form.maxMessageLength) : undefined,
          enable_threads: form.enableThreads,
          enable_reactions: form.enableReactions,
          enable_mentions: form.enableMentions,
          enable_embeds: form.enableEmbeds,
          enable_file_uploads: form.enableFileUploads,
        });
      }
      setShowForm(false);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (policy: ChatPolicy) => {
    if (!policy.entity?.id) return;
    if (!confirm(t('chat.policies.deleteConfirm', { name: policy.name }))) return;
    try {
      await chatApi.DeleteChatPolicy(policy.entity.id);
      setPolicies(prev => prev.filter(p => p.entity?.id !== policy.entity?.id));
    } catch (err) {
      setError(err);
    }
  };

  const handleLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!lookupId.trim()) return;
    setIsLookingUp(true);
    setLookupError(null);
    setLookupResult(null);
    try {
      const res = await chatApi.GetChatPolicyByEntity(lookupType.trim(), lookupId.trim());
      setLookupResult(res.policy ?? null);
    } catch (err) {
      setLookupError(err instanceof Error ? err.message : t('common.notFound'));
    } finally {
      setIsLookingUp(false);
    }
  };

  return (
    <div className="space-y-6">
      {error != null && <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} />}

      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{t('chat.policies.subtitle')}</p>
        <Button size="sm" onClick={startCreate}>{t('chat.policies.create')}</Button>
      </div>

      <FormDialog
        open={showForm}
        onClose={() => setShowForm(false)}
        onSubmit={handleSave}
        submitLabel={editingId ? t('common.saveChanges') : t('chat.policies.create')}
        canSubmit={!!form.name.trim()}
        isSubmitting={isSaving}
        dirty={!!form.name.trim() || !!form.description.trim()}
        aria-label={editingId ? t('common.edit') : t('chat.policies.create')}
      >
        <div className="space-y-3 px-3.5 pb-4 pt-2">
          <TitleInput
            value={form.name}
            onChange={value => setForm(p => ({ ...p, name: value }))}
            placeholder={t('common.name')}
            initialFocus
            onEnter={() => document.getElementById('chat-policy-description')?.focus()}
            aria-label={t('common.name')}
          />
          <Input
            id="chat-policy-description"
            label={t('common.description')}
            value={form.description}
            onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
          />
          <div className="grid grid-cols-2 gap-3">
            <Input label={t('chat.policies.scopeEntityType')} value={form.scopeEntityType} onChange={e => setForm(p => ({ ...p, scopeEntityType: e.target.value }))} placeholder="team" />
            <Input label={t('chat.policies.scopeEntityId')} value={form.scopeEntityId} onChange={e => setForm(p => ({ ...p, scopeEntityId: e.target.value }))} />
            <Input label={t('chat.policies.maxMembers')} type="number" value={form.maxMembersPerConversation} onChange={e => setForm(p => ({ ...p, maxMembersPerConversation: e.target.value }))} />
            <Input label={t('chat.policies.maxPinned')} type="number" value={form.maxPinnedMessages} onChange={e => setForm(p => ({ ...p, maxPinnedMessages: e.target.value }))} />
            <Input label={t('chat.policies.maxMessageLength')} type="number" value={form.maxMessageLength} onChange={e => setForm(p => ({ ...p, maxMessageLength: e.target.value }))} />
          </div>
          <div className="space-y-2 pt-1">
            {([
              ['enableThreads', 'chat.policies.enableThreads'],
              ['enableReactions', 'chat.policies.enableReactions'],
              ['enableMentions', 'chat.policies.enableMentions'],
              ['enableEmbeds', 'chat.policies.enableEmbeds'],
              ['enableFileUploads', 'chat.policies.enableFileUploads'],
            ] as const).map(([key, labelKey]) => (
              <ToggleSwitch
                key={key}
                label={t(labelKey)}
                checked={form[key]}
                onChange={value => setForm(p => ({ ...p, [key]: value }))}
              />
            ))}
          </div>
        </div>
      </FormDialog>

      <Table striped hoverable>
        <TableHeader>
          <TableRow>
            <TableHead>{t('common.name')}</TableHead>
            <TableHead>{t('chat.policies.scope')}</TableHead>
            <TableHead>{t('common.status')}</TableHead>
            <TableHead className="text-right">{t('common.actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableEmpty colSpan={4} message={t('common.loading')} />
          ) : policies.length === 0 ? (
            <TableEmpty colSpan={4} message={t('chat.policies.empty')} />
          ) : (
            policies.map(policy => (
              <TableRow key={policy.entity?.id}>
                <TableCell className="font-medium">{policy.name}</TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {policy.scope_entity_type || policy.scope_entity?.type_name || '—'}
                </TableCell>
                <TableCell>
                  <Badge variant={policy.is_active ? 'success' : 'secondary'} size="sm">
                    {policy.is_active ? t('common.active') : t('common.inactive')}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => startEdit(policy)}>{t('common.edit')}</Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(policy)}>{t('common.delete')}</Button>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <div className="rounded-lg border border-border p-4">
        <h3 className="mb-3 text-sm font-semibold text-foreground">{t('chat.policies.lookupTitle')}</h3>
        <form onSubmit={handleLookup} className="flex flex-wrap items-end gap-3">
          <Input label={t('chat.policies.scopeEntityType')} value={lookupType} onChange={e => setLookupType(e.target.value)} className="w-40" />
          <Input label={t('chat.policies.scopeEntityId')} value={lookupId} onChange={e => setLookupId(e.target.value)} className="w-64" required />
          <Button type="submit" size="sm" isLoading={isLookingUp}>{t('chat.policies.lookup')}</Button>
        </form>
        {lookupError && <p className="mt-2 text-sm text-destructive">{lookupError}</p>}
        {lookupResult && (
          <div className="mt-3 rounded-md bg-muted p-3 text-sm">
            <p className="font-medium text-foreground">{lookupResult.name}</p>
            {lookupResult.description && <p className="text-muted-foreground">{lookupResult.description}</p>}
          </div>
        )}
      </div>
    </div>
  );
}

export default ChatPoliciesPanel;
